import { reportEvent } from '@/lib/monitoring'
import { ServiceError } from './errors'

/**
 * Cliente HTTP único (inventario D15). La API del PMS es siempre POST + JSON y usa dos usuarios
 * Basic distintos: `web` (findreserve, savetravelregister…) y `partner` (pagos, extras, cambios).
 * Las credenciales vienen de VITE_PMS_BASIC_AUTH / VITE_PMS_PARTNER_BASIC_AUTH; nunca se escriben aquí.
 */
export type AuthKind = 'web' | 'partner' | 'none'

export interface PostOptions {
  auth?: AuthKind
  signal?: AbortSignal
  timeoutMs?: number
}

export const PMS_BOOTSTRAP_HOST = import.meta.env.VITE_PMS_BOOTSTRAP_HOST ?? 'servicesapp.availroom.com'
const PATH = '/sialdPMS/web/resourcescrossorigin/webcrossorigin/'

/** URL de un endpoint en el servidor de la reserva (`serverPath` = host devuelto por findreserve). */
export const pmsUrl = (serverPath: string, endpoint: string) => `https://${serverPath}${PATH}${endpoint}`
/** URL del endpoint de arranque (host fijo, ruta `sialdPMS_Services`). */
export const bootstrapUrl = (endpoint: string) => `https://${PMS_BOOTSTRAP_HOST}/sialdPMS_Services/web/resourcescrossorigin/webcrossorigin/${endpoint}`

function authHeader(kind: AuthKind): Record<string, string> {
  const value = kind === 'web' ? import.meta.env.VITE_PMS_BASIC_AUTH : kind === 'partner' ? import.meta.env.VITE_PMS_PARTNER_BASIC_AUTH : undefined
  return value ? { Authorization: `Basic ${value}` } : {}
}

/** Solo el nombre del endpoint (sin host ni query) para los eventos de Sentry. */
export const endpointOf = (url: string) => url.split('?')[0]!.split('/').filter(Boolean).pop() ?? ''

export async function postJson<T>(url: string, body: unknown, { auth = 'web', signal, timeoutMs = 20000 }: PostOptions = {}): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new ServiceError('offline')
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  signal?.addEventListener('abort', () => controller.abort(), { once: true })
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader(auth) },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    if (!response.ok) {
      reportEvent('http_error', { endpoint: endpointOf(url), status: response.status }, 'error')
      throw new ServiceError('server', `HTTP ${response.status}`, response.status)
    }
    return (await response.json()) as T
  } catch (e) {
    if (e instanceof ServiceError) throw e
    if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new ServiceError('offline')
    const aborted = e instanceof Error && e.name === 'AbortError' && signal?.aborted
    if (!aborted) reportEvent('http_error', { endpoint: endpointOf(url), status: null, code: e instanceof Error ? e.name : 'unknown' }, 'error')
    throw new ServiceError('server', e instanceof Error ? e.message : String(e))
  } finally {
    clearTimeout(timer)
  }
}
